import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Lock,
  Phone,
  Shield,
  X,
  Check,
  Eye,
  EyeOff,
  ChevronRight,
  Loader2
} from 'lucide-react'
import { authApi } from '../../lib/api'
import { usePageTitle } from '../../hooks/usePageTitle'

const AccountSecurity = () => {
  usePageTitle()
  const navigate = useNavigate()
  // 修改密码相关状态
  const [showPwdModal, setShowPwdModal] = useState(false)
  const [pwdStep, setPwdStep] = useState<1 | 2>(1) // 1: 输入手机号验证码, 2: 设置新密码
  const [pwdPhone, setPwdPhone] = useState('')
  const [pwdCode, setPwdCode] = useState('')
  const [pwdCountdown, setPwdCountdown] = useState(0)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [pwdError, setPwdError] = useState('')
  const [pwdLoading, setPwdLoading] = useState(false)
  const [showPwdSuccess, setShowPwdSuccess] = useState(false)

  // 打开修改密码弹窗时自动填充手机号
  const openPwdModal = () => {
    const userAccount = JSON.parse(localStorage.getItem('userAccount') || '{}')
    setPwdPhone(userAccount?.phone || '')
    setShowPwdModal(true)
  }

  // 更换手机号相关状态
  const [showPhoneModal, setShowPhoneModal] = useState(false)
  // 1: 验证当前手机号, 2: 填写新手机号+验证码, 3: 保存
  const [phoneStep, setPhoneStep] = useState<1 | 2 | 3>(1)
  const [currentPhone, setCurrentPhone] = useState('')
  const [currentPhoneCode, setCurrentPhoneCode] = useState('')
  const [phoneCountdown, setPhoneCountdown] = useState(0)
  const [newPhone, setNewPhone] = useState('')
  const [newPhoneCode, setNewPhoneCode] = useState('')
  const [newPhoneCountdown, setNewPhoneCountdown] = useState(0)
  const [phoneError, setPhoneError] = useState('')
  const [phoneLoading, setPhoneLoading] = useState(false)
  const [showPhoneSuccess, setShowPhoneSuccess] = useState(false)

  // 打开更换手机号弹窗时自动填充当前手机号
  const openPhoneModal = () => {
    const userAccount = JSON.parse(localStorage.getItem('userAccount') || '{}')
    setCurrentPhone(userAccount?.phone || '')
    setPhoneStep(1)
    setCurrentPhoneCode('')
    setPhoneCountdown(0)
    setNewPhone('')
    setNewPhoneCode('')
    setNewPhoneCountdown(0)
    setPhoneError('')
    setShowPhoneModal(true)
  }

  // 发送当前手机号验证码
  const sendCurrentPhoneCode = async () => {
    try {
      await authApi.sendCode(currentPhone, 'change_phone')
      setPhoneCountdown(60)
      setPhoneError('')
      const timer = setInterval(() => {
        setPhoneCountdown((prev) => {
          if (prev <= 1) { clearInterval(timer); return 0 }
          return prev - 1
        })
      }, 1000)
    } catch (e: any) {
      setPhoneError(e.message || '发送失败')
    }
  }

  // 验证当前手机号验证码 → 进入步骤2
  const verifyCurrentPhoneCode = async () => {
    if (!currentPhoneCode || currentPhoneCode.length !== 6) {
      setPhoneError('请输入6位验证码')
      return
    }
    try {
      await authApi.verifyCode(currentPhone, currentPhoneCode, 'change_phone')
      setPhoneStep(2)
      setPhoneError('')
    } catch (e: any) {
      setPhoneError(e.message || '验证失败')
    }
  }

  // 发送新手机号验证码
  const sendNewPhoneCode = async () => {
    if (!newPhone || !/^1[3-9]\d{9}$/.test(newPhone)) {
      setPhoneError('请输入正确的新手机号')
      return
    }
    try {
      await authApi.sendCode(newPhone, 'change_phone')
      setNewPhoneCountdown(60)
      setPhoneError('')
      const timer = setInterval(() => {
        setNewPhoneCountdown((prev) => {
          if (prev <= 1) { clearInterval(timer); return 0 }
          return prev - 1
        })
      }, 1000)
    } catch (e: any) {
      setPhoneError(e.message || '发送失败')
    }
  }

  // 保存新手机号
  const handleChangePhone = async () => {
    if (!newPhoneCode || newPhoneCode.length !== 6) {
      setPhoneError('请输入6位验证码')
      return
    }
    setPhoneLoading(true)
    try {
      await authApi.changePhone(newPhone, newPhoneCode)
      setShowPhoneModal(false)
      setShowPhoneSuccess(true)
    } catch (e: any) {
      setPhoneError(e.message || '修改失败')
    } finally {
      setPhoneLoading(false)
    }
  }

  // 发送修改密码验证码
  const sendPwdCode = async () => {
    if (!pwdPhone || !/^1[3-9]\d{9}$/.test(pwdPhone)) {
      setPwdError('请输入正确的手机号')
      return
    }
    try {
      await authApi.sendCode(pwdPhone, 'change_pwd')
      setPwdCountdown(60)
      setPwdError('')
      const timer = setInterval(() => {
        setPwdCountdown((prev) => {
          if (prev <= 1) { clearInterval(timer); return 0 }
          return prev - 1
        })
      }, 1000)
    } catch (e: any) {
      setPwdError(e.message || '发送失败')
    }
  }

  // 验证修改密码验证码
  const verifyPwdCode = async () => {
    if (!pwdCode || pwdCode.length !== 6) {
      setPwdError('请输入6位验证码')
      return
    }
    try {
      await authApi.verifyCode(pwdPhone, pwdCode, 'change_pwd')
      setPwdStep(2)
      setPwdError('')
    } catch (e: any) {
      setPwdError(e.message || '验证失败')
    }
  }

  // 提交新密码（验证码已验证，直接修改）
  const handleChangePassword = async () => {
    setPwdError('')
    if (newPassword.length < 8 || newPassword.length > 20) {
      setPwdError('密码8-20位')
      return
    }
    if (!/[a-zA-Z]/.test(newPassword)) {
      setPwdError('密码必须包含字母')
      return
    }
    if (!/\d/.test(newPassword)) {
      setPwdError('密码必须包含数字')
      return
    }
    if (newPassword !== confirmPassword) {
      setPwdError('两次密码不一致')
      return
    }
    setPwdLoading(true)
    try {
      await authApi.changePasswordSimple(newPassword)
      setShowPwdModal(false)
      setShowPwdSuccess(true)
    } catch (e: any) {
      setPwdError(e.message || '修改失败')
    } finally {
      setPwdLoading(false)
    }
  }

  // 关闭弹窗重置状态
  const closePwdModal = () => {
    setShowPwdModal(false)
    setPwdStep(1)
    setPwdPhone('')
    setPwdCode('')
    setPwdCountdown(0)
    setNewPassword('')
    setConfirmPassword('')
    setPwdError('')
  }

  // 密码强度检查
  const checkPwdStrength = (pwd: string) => ({
    length: pwd.length >= 8 && pwd.length <= 20,
    letter: /[a-zA-Z]/.test(pwd),
    number: /\d/.test(pwd),
  })

  return (
    <div className="min-h-screen bg-gray-50 pb-6">
      {/* 顶部 */}
      <div className="bg-gradient-to-br from-green-600 to-green-700 px-4 pt-6 pb-8">
        <div className="flex items-center justify-center">
          <span className="text-white font-bold text-base">账号安全</span>
        </div>
        <p className="text-green-100 text-sm text-center mt-2">
          保护您的账号安全
        </p>
      </div>

      {/* 功能列表 */}
      <div className="px-4 -mt-4">
        <div className="bg-white rounded-xl shadow-md divide-y divide-gray-100">
          <div
            onClick={openPwdModal}
            className="flex items-center p-4 hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
              <Lock className="w-5 h-5 text-purple-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-800">修改密码</p>
              <p className="text-xs text-gray-500 mt-0.5">通过短信验证码修改</p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </div>

          <div
            onClick={openPhoneModal}
            className="flex items-center p-4 hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
              <Phone className="w-5 h-5 text-green-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-800">更换手机号</p>
              <p className="text-xs text-gray-500 mt-0.5">更换后可通过新手机号登录</p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </div>
        </div>
      </div>

      {/* 安全提示 */}
      <div className="px-4 mt-6">
        <div className="bg-green-50 rounded-xl p-4">
          <div className="flex items-start space-x-3">
            <Shield className="w-5 h-5 text-green-600 mt-0.5" />
            <div className="text-sm text-green-700">
              <p className="font-medium">安全提示</p>
              <ul className="mt-2 space-y-1 text-green-600">
                <li>• 密码8-20位，必须包含字母和数字</li>
                <li>• 不要使用过于简单的密码</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* 底部版权 */}
      <div className="px-4 mt-8 text-center">
        <p className="text-xs text-gray-400">{localStorage.getItem('copyright') || '© 2026 眉山疾控'}</p>
      </div>

      {/* 修改密码弹窗 */}
      {showPwdModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-white">修改密码</h3>
                <p className="text-green-100 text-sm">通过短信验证码修改</p>
              </div>
              <button
                onClick={closePwdModal}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {pwdStep === 1 && (
                <>
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                    <p className="text-sm text-green-800">
                      验证码将发送到您绑定的手机号
                    </p>
                    <p className="text-green-600 font-medium mt-1">{pwdPhone}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      验证码
                    </label>
                    <div className="flex space-x-3">
                      <input
                        type="text"
                        placeholder="请输入验证码"
                        value={pwdCode}
                        onChange={(e) => setPwdCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="flex-1 px-4 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                      <button
                        onClick={sendPwdCode}
                        disabled={pwdCountdown > 0}
                        className={`px-4 py-4 rounded-xl font-medium ${
                          pwdCountdown > 0
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-green-700 text-white hover:bg-green-800'
                        }`}
                      >
                        {pwdCountdown > 0 ? `${pwdCountdown}秒` : '获取验证码'}
                      </button>
                    </div>
                  </div>

                  {pwdError && (
                    <div className="text-red-500 text-sm bg-red-50 p-3 rounded-xl">
                      {pwdError}
                    </div>
                  )}
                </>
              )}

              {pwdStep === 2 && (
                <>
                  <div className="bg-green-50 rounded-xl p-4 text-center">
                    <Check className="w-12 h-12 text-green-600 mx-auto mb-2" />
                    <p className="text-gray-700">验证码已验证通过</p>
                    <p className="text-green-600 font-medium mt-1">{pwdPhone}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      新密码
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="请输入新密码（8-20位）"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full pl-12 pr-12 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      确认新密码
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="请再次输入新密码"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full pl-12 pr-12 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                  </div>

                  <div className="bg-green-50 rounded-xl p-4">
                    <p className="text-sm text-green-800">
                      <strong>密码要求：</strong>
                    </p>
                    <ul className="text-sm mt-2 space-y-1">
                      <li className="flex items-center">
                        {checkPwdStrength(newPassword).length ? (
                          <Check className="w-4 h-4 mr-2 text-green-500" />
                        ) : (
                          <X className="w-4 h-4 mr-2 text-red-500" />
                        )}
                        <span className={checkPwdStrength(newPassword).length ? 'text-green-600' : 'text-red-500'}>8-20位字符</span>
                      </li>
                      <li className="flex items-center">
                        {checkPwdStrength(newPassword).letter ? (
                          <Check className="w-4 h-4 mr-2 text-green-500" />
                        ) : (
                          <X className="w-4 h-4 mr-2 text-red-500" />
                        )}
                        <span className={checkPwdStrength(newPassword).letter ? 'text-green-600' : 'text-red-500'}>包含字母</span>
                      </li>
                      <li className="flex items-center">
                        {checkPwdStrength(newPassword).number ? (
                          <Check className="w-4 h-4 mr-2 text-green-500" />
                        ) : (
                          <X className="w-4 h-4 mr-2 text-red-500" />
                        )}
                        <span className={checkPwdStrength(newPassword).number ? 'text-green-600' : 'text-red-500'}>包含数字</span>
                      </li>
                    </ul>
                  </div>

                  {pwdError && (
                    <div className="text-red-500 text-sm bg-red-50 p-3 rounded-xl">
                      {pwdError}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="p-6 border-t flex space-x-3">
              <button
                onClick={closePwdModal}
                className="flex-1 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                取消
              </button>
              <button
                onClick={pwdStep === 1 ? verifyPwdCode : handleChangePassword}
                disabled={pwdLoading}
                className="flex-1 py-3 bg-green-700 text-white rounded-xl hover:bg-green-800 transition-colors disabled:opacity-50 flex items-center justify-center"
              >
                {pwdLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : pwdStep === 1 ? '下一步' : '保存修改'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 修改密码成功弹窗 */}
      {showPwdSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-sm mx-4 shadow-2xl overflow-hidden">
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">密码修改成功</h3>
              <p className="text-gray-500 mb-6">您的密码已成功修改</p>
              <button
                onClick={() => setShowPwdSuccess(false)}
                className="w-full py-3 bg-green-700 text-white rounded-xl font-medium hover:bg-green-800 transition-colors"
              >
                确定
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 更换手机号弹窗 */}
      {showPhoneModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-white">更换手机号</h3>
                <p className="text-green-100 text-sm">
                  {phoneStep === 1 ? '验证当前手机号' : '填写新手机号'}
                </p>
              </div>
              <button
                onClick={() => setShowPhoneModal(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* 步骤1: 验证当前手机号 */}
              {phoneStep === 1 && (
                <>
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                    <p className="text-sm text-green-800">验证码将发送到您绑定的手机号</p>
                    <p className="text-green-600 font-medium mt-1">{currentPhone}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      验证码
                    </label>
                    <div className="flex space-x-3">
                      <input
                        type="text"
                        placeholder="请输入验证码"
                        value={currentPhoneCode}
                        onChange={(e) => setCurrentPhoneCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="flex-1 px-4 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                      <button
                        onClick={sendCurrentPhoneCode}
                        disabled={phoneCountdown > 0}
                        className={`px-4 py-4 rounded-xl font-medium ${
                          phoneCountdown > 0
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-green-700 text-white hover:bg-green-800'
                        }`}
                      >
                        {phoneCountdown > 0 ? `${phoneCountdown}秒` : '获取验证码'}
                      </button>
                    </div>
                  </div>

                  {phoneError && (
                    <div className="text-red-500 text-sm bg-red-50 p-3 rounded-xl">
                      {phoneError}
                    </div>
                  )}
                </>
              )}

              {/* 步骤2: 填写新手机号 */}
              {phoneStep === 2 && (
                <>
                  <div className="bg-green-50 rounded-xl p-4 text-center">
                    <Check className="w-12 h-12 text-green-600 mx-auto mb-2" />
                    <p className="text-gray-700">当前手机号验证通过</p>
                    <p className="text-green-600 font-medium mt-1">请填写并验证新手机号</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      新手机号
                    </label>
                    <div className="flex space-x-3">
                      <input
                        type="tel"
                        placeholder="请输入新手机号"
                        value={newPhone}
                        onChange={(e) => setNewPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
                        className="flex-1 px-4 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                      <button
                        onClick={sendNewPhoneCode}
                        disabled={newPhoneCountdown > 0 || !newPhone}
                        className={`px-4 py-4 rounded-xl font-medium ${
                          newPhoneCountdown > 0 || !newPhone
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-green-700 text-white hover:bg-green-800'
                        }`}
                      >
                        {newPhoneCountdown > 0 ? `${newPhoneCountdown}秒` : '获取验证码'}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      新手机验证码
                    </label>
                    <input
                      type="text"
                      placeholder="请输入验证码"
                      value={newPhoneCode}
                      onChange={(e) => setNewPhoneCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="w-full px-4 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  {phoneError && (
                    <div className="text-red-500 text-sm bg-red-50 p-3 rounded-xl">
                      {phoneError}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="p-6 border-t flex space-x-3">
              <button
                onClick={() => setShowPhoneModal(false)}
                className="flex-1 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                取消
              </button>
              <button
                onClick={phoneStep === 1 ? verifyCurrentPhoneCode : handleChangePhone}
                disabled={phoneLoading}
                className="flex-1 py-3 bg-green-700 text-white rounded-xl hover:bg-green-800 transition-colors disabled:opacity-50 flex items-center justify-center"
              >
                {phoneLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : phoneStep === 1 ? '下一步' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 修改手机号成功弹窗 */}
      {showPhoneSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-sm mx-4 shadow-2xl overflow-hidden">
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">手机号修改成功</h3>
              <p className="text-gray-500 mb-6">您的手机号已成功修改</p>
              <button
                onClick={() => setShowPhoneSuccess(false)}
                className="w-full py-3 bg-green-700 text-white rounded-xl font-medium hover:bg-green-800 transition-colors"
              >
                确定
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AccountSecurity
